import { useState, useEffect, useCallback } from 'react';

// Convert VAPID key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const usePushNotifications = (userId, apiUrl) => {
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const checkSubscription = useCallback(async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
    } catch (error) {
      setIsSubscribed(false);
    }
  }, []); // No external dependencies - all operations are self-contained

  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
      checkSubscription();
    }
  }, [checkSubscription]);

  const subscribeToPush = useCallback(async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      if (!('serviceWorker' in navigator)) {
        throw new Error('Service workers are not supported in this browser');
      }

      if (!('PushManager' in window)) {
        throw new Error('Push notifications are not supported in this browser');
      }

      let registration;
      try {
        registration = await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;
      } catch (error) {
        throw new Error('Failed to register service worker');
      }

      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);

      if (permissionResult !== 'granted') {
        throw new Error('Notification permission denied');
      }

      const vapidResponse = await fetch(`${apiUrl}/api/push/vapid-public-key`);
      if (!vapidResponse.ok) {
        throw new Error('Failed to get VAPID public key');
      }
      const { publicKey } = await vapidResponse.json();

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });

      const subscribeResponse = await fetch(`${apiUrl}/api/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          subscription: subscription.toJSON()
        })
      });

      if (!subscribeResponse.ok) {
        throw new Error('Failed to save subscription');
      }

      setIsSubscribed(true);
      setMessage({
        type: 'success',
        text: 'Successfully subscribed to notifications! You&apos;ll receive appointment reminders and special offers.'
      });

    } catch (error) {
      setMessage({
        type: 'error',
        text: error.message || 'Failed to enable notifications. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  }, [userId, apiUrl]); // Include all external dependencies

  const unsubscribeFromPush = useCallback(async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();

        await fetch(`${apiUrl}/api/push/unsubscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: userId,
            subscription: subscription.toJSON()
          })
        });
      }

      setIsSubscribed(false);
      setMessage({
        type: 'success',
        text: 'Successfully unsubscribed from notifications.'
      });

    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Failed to unsubscribe. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  }, [userId, apiUrl]); // Include all external dependencies

  return {
    permission,
    isSubscribed,
    loading,
    message,
    subscribeToPush,
    unsubscribeFromPush
  };
};
